/**
 * DemoModeToggle - Settings toggle for enabling/disabling demo mode
 *
 * When enabled, chat queries are automatically scoped to "Valley Equipment"
 * for demonstration purposes. Uses shadcn/ui Switch for accessibility.
 */

import { useDemoMode, DEMO_CLIENT } from '@/hooks/useDemoMode';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Presentation } from 'lucide-react';

interface DemoModeToggleProps {
  className?: string;
}

export function DemoModeToggle({ className }: DemoModeToggleProps) {
  const { isDemoMode, setDemoMode } = useDemoMode();

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2 px-2 text-sm font-medium text-muted-foreground">
        <Presentation className="h-4 w-4" />
        <span>Demo Mode</span>
      </div>
      <Label
        htmlFor="demo-mode-toggle"
        className={cn(
          'flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer transition-all',
          'hover:border-primary/50',
          'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary',
          isDemoMode ? 'border-primary bg-primary/5' : 'border-border',
        )}
      >
        <div className="flex-1 select-none">
          <span
            className={cn(
              'font-medium block',
              isDemoMode ? 'text-foreground' : 'text-foreground/80',
            )}
          >
            {isDemoMode ? 'Enabled' : 'Disabled'}
          </span>
          <p className="text-xs text-muted-foreground font-normal">
            {isDemoMode
              ? `Queries scoped to ${DEMO_CLIENT}`
              : 'Enable to scope queries to demo client'}
          </p>
        </div>
        <Switch
          id="demo-mode-toggle"
          checked={isDemoMode}
          onCheckedChange={setDemoMode}
          aria-describedby="demo-mode-description"
        />
      </Label>
      <p id="demo-mode-description" className="sr-only">
        When enabled, all chat queries will be filtered to show only results
        from {DEMO_CLIENT} for demonstration purposes.
      </p>
    </div>
  );
}
